import React, { useState, lazy, Suspense, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Heart, Menu, X, LogOut, User, Bell, MessageSquare, Search, Settings, ChevronDown } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../hooks/useSupabaseData';

// Lazy load heavy components
const LazyNotificationPanel = lazy(() => import('./NotificationPanel'));

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isNotificationPanelOpen, setIsNotificationPanelOpen] = useState(false);
  const [activeNotificationTab, setActiveNotificationTab] = useState<'notifications' | 'messages'>('notifications');
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const { user, profile, signOut } = useAuth();
  const { notifications } = useNotifications();

  // Track scroll position for dynamic header styling
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setIsScrolled(scrollPosition > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMenuOpen(false);
    setIsUserMenuOpen(false);
    setIsSearchOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMenuOpen]);

  // Mock data for messages count (in real app, this would come from a hook)
  const mockUnreadMessagesCount = 3;
  const unreadNotificationsCount = notifications.filter(n => !n.read).length;

  const navigation = [
    { name: 'Home', href: '/' },
    { name: 'About Us', href: '/about' },
    { name: 'Become a Donor', href: '/register-donor' },
    { name: 'Education', href: '/education' },
  ];

  const userNavigation = [
    ...(profile?.role === 'recipient' ? [{ name: 'My Dashboard', href: '/recipient-dashboard' }] : []),
    ...(profile?.role === 'hospital' ? [{ name: 'Hospital Portal', href: '/hospital-portal' }] : []),
  ];

  const isCurrentPath = (path: string) => location.pathname === path;

  const handleLogout = async () => {
    try {
      await signOut();
      setIsUserMenuOpen(false);
      setIsMenuOpen(false);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const openNotificationPanel = (tab: 'notifications' | 'messages') => {
    setActiveNotificationTab(tab);
    setIsNotificationPanelOpen(true);
    setIsMenuOpen(false);
  };

  const closeAllMenus = () => {
    setIsMenuOpen(false);
    setIsUserMenuOpen(false);
    setIsSearchOpen(false);
  };

  // Enhanced navigation click handler to scroll to top
  const handleNavClick = () => {
    closeAllMenus();
    // Scroll to top smoothly
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth'
    });
  };

  // Dynamic header classes based on scroll position
  const headerClasses = `
    ${isScrolled 
      ? 'bg-white/95 backdrop-blur-xl shadow-xl border-b border-gray-200/50' 
      : 'bg-white/98 backdrop-blur-lg shadow-lg border-b border-gray-100'
    } 
    sticky top-0 z-50 transition-all duration-300
  `;

  return (
    <>
      <header className={headerClasses}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Enhanced Logo with better contrast */}
            <Link to="/" className="flex items-center space-x-3 group" onClick={handleNavClick}>
              <div className="relative">
                <div className={`p-2.5 bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800 rounded-xl group-hover:from-blue-700 group-hover:to-blue-900 transition-all duration-300 shadow-lg group-hover:shadow-xl group-hover:scale-105 ${isScrolled ? 'shadow-xl' : ''}`}>
                  <Heart className="h-6 w-6 text-white group-hover:animate-pulse" />
                </div>
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full animate-pulse shadow-sm"></div>
              </div>
              <div className="flex flex-col">
                <span className={`text-xl font-bold transition-colors ${isScrolled ? 'text-gray-900' : 'text-gray-900'} group-hover:text-blue-600`}>LifeShare</span>
                <span className={`text-xs transition-colors font-medium ${isScrolled ? 'text-gray-600' : 'text-gray-500'} group-hover:text-blue-500 hidden sm:block`}>Saving Lives Together</span>
              </div>
            </Link>

            {/* Enhanced Desktop Navigation with better contrast */}
            <nav className="hidden lg:flex items-center space-x-1">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={handleNavClick}
                  className={`relative px-4 py-2.5 rounded-xl text-sm font-semibold transition-all duration-300 hover:scale-105 group ${
                    isCurrentPath(item.href)
                      ? `text-blue-700 ${isScrolled ? 'bg-blue-50 shadow-md border border-blue-200' : 'bg-blue-50 shadow-md border border-blue-100'}`
                      : `${isScrolled ? 'text-gray-800 hover:text-blue-600 hover:bg-gray-50 border border-transparent hover:border-gray-300' : 'text-gray-700 hover:text-blue-600 hover:bg-gray-50 border border-transparent hover:border-gray-200'}`
                  }`}
                >
                  {item.name}
                  {isCurrentPath(item.href) && (
                    <div className="absolute bottom-1 left-1/2 transform -translate-x-1/2 w-1.5 h-1.5 bg-blue-600 rounded-full animate-pulse"></div>
                  )}
                </Link>
              ))}
              
              {user && userNavigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={handleNavClick}
                  className={`relative px-4 py-2.5 rounded-xl text-sm font-semibold transition-all duration-300 hover:scale-105 ${
                    isCurrentPath(item.href)
                      ? `text-blue-700 ${isScrolled ? 'bg-blue-50 shadow-md border border-blue-200' : 'bg-blue-50 shadow-md border border-blue-100'}`
                      : `${isScrolled ? 'text-gray-800 hover:text-blue-600 hover:bg-gray-50 border border-transparent hover:border-gray-300' : 'text-gray-700 hover:text-blue-600 hover:bg-gray-50 border border-transparent hover:border-gray-200'}`
                  }`}
                >
                  {item.name}
                  {isCurrentPath(item.href) && (
                    <div className="absolute bottom-1 left-1/2 transform -translate-x-1/2 w-1.5 h-1.5 bg-blue-600 rounded-full animate-pulse"></div>
                  )}
                </Link>
              ))}
            </nav>

            {/* Enhanced Right Side Actions with better contrast */}
            <div className="flex items-center space-x-2">
              {/* Enhanced Search Button - Hidden on mobile */}
              <div className="relative hidden sm:block">
                <button
                  onClick={() => setIsSearchOpen(!isSearchOpen)}
                  className={`p-2.5 rounded-xl transition-all duration-300 group hover:scale-110 ${
                    isScrolled 
                      ? 'hover:bg-gray-100 border border-transparent hover:border-gray-300' 
                      : 'hover:bg-gray-100 border border-transparent hover:border-gray-200'
                  }`}
                >
                  <Search className={`h-5 w-5 transition-colors ${isScrolled ? 'text-gray-700 group-hover:text-blue-600' : 'text-gray-600 group-hover:text-blue-600'}`} />
                </button>
                
                {/* Enhanced Search Dropdown */}
                {isSearchOpen && (
                  <div className="absolute top-full right-0 mt-2 w-80 bg-white/98 backdrop-blur-xl rounded-xl shadow-xl border border-gray-200 p-4 animate-fade-in-up z-50">
                    <div className="relative">
                      <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search donors, recipients, resources..."
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm transition-all duration-300"
                        autoFocus
                      />
                    </div>
                    <div className="mt-3 text-xs text-gray-500 font-medium">
                      Quick search across all platform resources
                    </div>
                  </div>
                )}
              </div>

              {user && profile ? (
                <>
                  {/* Enhanced Messages Button - Hidden on mobile */}
                  <div className="relative hidden sm:block">
                    <button 
                      onClick={() => openNotificationPanel('messages')}
                      className={`relative p-2.5 rounded-xl transition-all duration-300 group hover:scale-110 ${
                        isScrolled 
                          ? 'hover:bg-gray-100 border border-transparent hover:border-gray-300' 
                          : 'hover:bg-gray-100 border border-transparent hover:border-gray-200'
                      }`}
                    >
                      <MessageSquare className={`h-5 w-5 transition-colors ${isScrolled ? 'text-gray-700 group-hover:text-green-600' : 'text-gray-600 group-hover:text-green-600'}`} />
                      {mockUnreadMessagesCount > 0 && (
                        <span className="absolute -top-1 -right-1 bg-green-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-bold border-2 border-white animate-pulse shadow-lg">
                          {mockUnreadMessagesCount}
                        </span>
                      )}
                    </button>
                  </div>

                  {/* Enhanced Notifications Button - Hidden on mobile */}
                  <div className="relative hidden sm:block">
                    <button 
                      onClick={() => openNotificationPanel('notifications')}
                      className={`relative p-2.5 rounded-xl transition-all duration-300 group hover:scale-110 ${
                        isScrolled 
                          ? 'hover:bg-gray-100 border border-transparent hover:border-gray-300' 
                          : 'hover:bg-gray-100 border border-transparent hover:border-gray-200'
                      }`}
                    >
                      <Bell className={`h-5 w-5 transition-colors ${isScrolled ? 'text-gray-700 group-hover:text-red-600' : 'text-gray-600 group-hover:text-red-600'}`} />
                      {unreadNotificationsCount > 0 && (
                        <span className="absolute -top-1 -right-1 bg-red-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-bold border-2 border-white animate-bounce shadow-lg">
                          {unreadNotificationsCount}
                        </span>
                      )}
                    </button>
                  </div>
                  
                  {/* Enhanced User Menu with Fixed Background - Hidden on mobile */}
                  <div className="relative hidden sm:block">
                    <button
                      onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                      className={`flex items-center space-x-3 rounded-xl px-3 py-2.5 transition-all duration-300 hover:shadow-md group ${
                        isScrolled 
                          ? 'bg-gradient-to-r from-gray-50 to-gray-100 hover:from-gray-100 hover:to-gray-200 border border-gray-300 hover:border-gray-400' 
                          : 'bg-gradient-to-r from-gray-50 to-gray-100 hover:from-gray-100 hover:to-gray-200 border border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="p-1.5 bg-blue-100 rounded-full group-hover:bg-blue-200 transition-colors">
                        <User className="h-4 w-4 text-blue-600" />
                      </div>
                      <div className="text-left">
                        <span className={`text-sm font-semibold block truncate max-w-24 ${isScrolled ? 'text-gray-900' : 'text-gray-900'}`}>{profile.full_name}</span>
                        <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full capitalize font-medium">
                          {profile.role}
                        </span>
                      </div>
                      <ChevronDown className={`h-4 w-4 transition-transform duration-300 ${isScrolled ? 'text-gray-600' : 'text-gray-500'} ${isUserMenuOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {/* Enhanced User Dropdown with Fixed Background */}
                    {isUserMenuOpen && (
                      <div className="absolute top-full right-0 mt-2 w-64 bg-white rounded-xl shadow-xl border border-gray-200 py-2 animate-fade-in-up z-50">
                        <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
                          <p className="text-sm font-semibold text-gray-900 truncate">{profile.full_name}</p>
                          <p className="text-xs text-gray-500 truncate">{profile.email}</p>
                          <span className="inline-block mt-1 text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full capitalize font-medium">
                            {profile.role}
                          </span>
                        </div>
                        
                        <div className="py-2">
                          <Link
                            to="/account-settings"
                            onClick={closeAllMenus}
                            className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-3 transition-colors"
                          >
                            <Settings className="h-4 w-4" />
                            <span>Account Settings</span>
                          </Link>
                          <Link
                            to="/edit-profile"
                            onClick={closeAllMenus}
                            className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-3 transition-colors"
                          >
                            <User className="h-4 w-4" />
                            <span>Edit Profile</span>
                          </Link>
                        </div>
                        
                        <div className="border-t border-gray-100 pt-2">
                          <button
                            onClick={handleLogout}
                            className="w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 flex items-center space-x-3 transition-colors"
                          >
                            <LogOut className="h-4 w-4" />
                            <span>Sign Out</span>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <Link
                  to="/login"
                  onClick={handleNavClick}
                  className={`bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-4 sm:px-6 py-2.5 rounded-xl text-sm font-bold transition-all duration-300 transform hover:scale-105 btn-animate border border-blue-600 ${
                    isScrolled ? 'shadow-xl hover:shadow-2xl' : 'shadow-lg hover:shadow-xl'
                  }`}
                >
                  Sign In
                </Link>
              )}

              {/* Enhanced Bolt.new Logo - Hidden on mobile */}
              <div className="hidden md:block">
                <a 
                  href="https://bolt.new" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className={`flex items-center space-x-2 p-2.5 rounded-xl transition-all duration-300 group hover:scale-105 ${
                    isScrolled 
                      ? 'hover:bg-gray-100 border border-transparent hover:border-gray-300' 
                      : 'hover:bg-gray-100 border border-transparent hover:border-gray-200'
                  }`}
                >
                  <img 
                    src="/white_circle_360x360.png" 
                    alt="Bolt.new" 
                    className={`h-8 w-8 rounded-full transition-shadow duration-300 ${
                      isScrolled ? 'shadow-md group-hover:shadow-lg' : 'shadow-md group-hover:shadow-lg'
                    }`}
                  />
                  <span className={`text-xs transition-colors font-medium hidden lg:block ${
                    isScrolled ? 'text-gray-700 group-hover:text-blue-600' : 'text-gray-600 group-hover:text-blue-600'
                  }`}>
                    Bolt.new
                  </span>
                </a>
              </div>

              {/* Enhanced Mobile menu button */}
              <div className="lg:hidden">
                <button
                  type="button"
                  onClick={() => setIsMenuOpen(!isMenuOpen)}
                  className={`p-2.5 rounded-xl transition-all duration-300 hover:scale-110 ${
                    isScrolled 
                      ? 'text-gray-700 hover:text-gray-900 hover:bg-gray-100 border border-transparent hover:border-gray-300' 
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100 border border-transparent hover:border-gray-200'
                  }`}
                  aria-label="Toggle menu"
                >
                  {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Enhanced Mobile Navigation Overlay */}
      {isMenuOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black/50 backdrop-blur-sm" 
            onClick={() => setIsMenuOpen(false)}
          />
          
          {/* Mobile Menu Panel - Fixed with white background and proper contrast */}
          <div className="fixed top-16 left-0 right-0 bottom-0 bg-white overflow-y-auto">
            <div className="px-4 py-6 space-y-6">
              {/* Mobile Search */}
              <div className="relative">
                <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search..."
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm transition-all duration-300 bg-white text-gray-900"
                />
              </div>

              {/* Navigation Links - Fixed with proper contrast */}
              <div className="space-y-2">
                {navigation.map((item, index) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={handleNavClick}
                    className={`block px-4 py-4 rounded-xl text-base font-semibold transition-all duration-300 animate-fade-in-up hover:scale-105 ${
                      isCurrentPath(item.href)
                        ? 'bg-blue-100 text-blue-700 border-l-4 border-blue-600 shadow-sm'
                        : 'text-gray-900 hover:bg-gray-100 border border-transparent hover:border-gray-300 hover:text-blue-600'
                    }`}
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <div className="flex items-center justify-between">
                      <span>{item.name}</span>
                      {isCurrentPath(item.href) && (
                        <div className="w-2 h-2 bg-blue-600 rounded-full animate-pulse"></div>
                      )}
                    </div>
                  </Link>
                ))}
                
                {user && userNavigation.map((item, index) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={handleNavClick}
                    className={`block px-4 py-4 rounded-xl text-base font-semibold transition-all duration-300 animate-fade-in-up hover:scale-105 ${
                      isCurrentPath(item.href)
                        ? 'bg-blue-100 text-blue-700 border-l-4 border-blue-600 shadow-sm'
                        : 'text-gray-900 hover:bg-gray-100 border border-transparent hover:border-gray-300 hover:text-blue-600'
                    }`}
                    style={{ animationDelay: `${(navigation.length + index) * 0.1}s` }}
                  >
                    <div className="flex items-center justify-between">
                      <span>{item.name}</span>
                      {isCurrentPath(item.href) && (
                        <div className="w-2 h-2 bg-blue-600 rounded-full animate-pulse"></div>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
              
              {user && profile ? (
                <div className="space-y-4 pt-4 border-t border-gray-200">
                  {/* Mobile Messages and Notifications */}
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => {
                        openNotificationPanel('messages');
                      }}
                      className="flex items-center justify-center space-x-2 bg-white rounded-xl border border-gray-200 px-3 py-4 hover:bg-gray-50 transition-all duration-300 shadow-sm hover:shadow-md"
                    >
                      <MessageSquare className="h-5 w-5 text-green-600" />
                      <span className="text-sm font-medium text-gray-900">Messages</span>
                      {mockUnreadMessagesCount > 0 && (
                        <span className="bg-green-600 text-white text-xs rounded-full px-2 py-0.5 animate-pulse">
                          {mockUnreadMessagesCount}
                        </span>
                      )}
                    </button>
                    <button
                      onClick={() => {
                        openNotificationPanel('notifications');
                      }}
                      className="flex items-center justify-center space-x-2 bg-white rounded-xl border border-gray-200 px-3 py-4 hover:bg-gray-50 transition-all duration-300 shadow-sm hover:shadow-md"
                    >
                      <Bell className="h-5 w-5 text-red-600" />
                      <span className="text-sm font-medium text-gray-900">Alerts</span>
                      {unreadNotificationsCount > 0 && (
                        <span className="bg-red-600 text-white text-xs rounded-full px-2 py-0.5 animate-bounce">
                          {unreadNotificationsCount}
                        </span>
                      )}
                    </button>
                  </div>

                  {/* Mobile Bolt.new Logo */}
                  <div className="animate-fade-in-up" style={{ animationDelay: '0.35s' }}>
                    <a 
                      href="https://bolt.new" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center justify-center space-x-3 bg-white rounded-xl border border-gray-200 px-3 py-4 hover:bg-gray-50 transition-all duration-300 shadow-sm hover:shadow-md"
                    >
                      <img 
                        src="/white_circle_360x360.png" 
                        alt="Bolt.new" 
                        className="h-6 w-6 rounded-full shadow-sm"
                      />
                      <span className="text-sm font-medium text-gray-700">Powered by Bolt.new</span>
                    </a>
                  </div>

                  {/* User Profile Card - Fixed with proper contrast */}
                  <div className="bg-gradient-to-r from-gray-50 to-gray-100 border border-gray-300 rounded-xl p-4 animate-fade-in-up shadow-sm" style={{ animationDelay: '0.4s' }}>
                    <p className="text-sm font-bold text-gray-900 truncate">{profile.full_name}</p>
                    <p className="text-xs text-gray-600 truncate">{profile.email}</p>
                    <span className="inline-block mt-2 text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full capitalize font-medium">
                      {profile.role}
                    </span>
                  </div>
                  
                  {/* Mobile Account Settings and Edit Profile - Fixed with proper contrast */}
                  <div className="space-y-2 animate-fade-in-up" style={{ animationDelay: '0.45s' }}>
                    <Link
                      to="/account-settings"
                      onClick={handleNavClick}
                      className="flex items-center space-x-3 w-full text-left px-4 py-3 text-sm text-gray-900 hover:bg-gray-50 rounded-xl transition-all duration-300 font-semibold border border-gray-200"
                    >
                      <Settings className="h-4 w-4 text-gray-600" />
                      <span>Account Settings</span>
                    </Link>
                    <Link
                      to="/edit-profile"
                      onClick={handleNavClick}
                      className="flex items-center space-x-3 w-full text-left px-4 py-3 text-sm text-gray-900 hover:bg-gray-50 rounded-xl transition-all duration-300 font-semibold border border-gray-200"
                    >
                      <User className="h-4 w-4 text-gray-600" />
                      <span>Edit Profile</span>
                    </Link>
                  </div>
                  
                  <button
                    onClick={handleLogout}
                    className="w-full text-left px-4 py-3 text-sm text-red-600 hover:bg-red-50 rounded-xl transition-all duration-300 font-semibold animate-fade-in-up flex items-center space-x-3"
                    style={{ animationDelay: '0.5s' }}
                  >
                    <LogOut className="h-4 w-4" />
                    <span>Sign Out</span>
                  </button>
                </div>
              ) : (
                <div className="space-y-4 pt-4 border-t border-gray-200">
                  {/* Mobile Bolt.new Logo for non-logged in users */}
                  <div className="animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
                    <a 
                      href="https://bolt.new" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center justify-center space-x-3 bg-white rounded-xl border border-gray-200 px-3 py-4 hover:bg-gray-50 transition-all duration-300 shadow-sm hover:shadow-md"
                    >
                      <img 
                        src="/white_circle_360x360.png" 
                        alt="Bolt.new" 
                        className="h-6 w-6 rounded-full shadow-sm"
                      />
                      <span className="text-sm font-medium text-gray-700">Powered by Bolt.new</span>
                    </a>
                  </div>
                  
                  <Link
                    to="/login"
                    onClick={handleNavClick}
                    className="block w-full text-center bg-gradient-to-r from-blue-600 to-blue-700 text-white px-4 py-4 rounded-xl text-sm font-bold hover:from-blue-700 hover:to-blue-800 transition-all duration-300 animate-fade-in-up btn-animate shadow-lg"
                    style={{ animationDelay: '0.3s' }}
                  >
                    Sign In
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Notification Panel with Suspense */}
      <Suspense fallback={<div />}>
        <LazyNotificationPanel
          isOpen={isNotificationPanelOpen}
          onClose={() => setIsNotificationPanelOpen(false)}
          activeTab={activeNotificationTab}
          onTabChange={setActiveNotificationTab}
        />
      </Suspense>

      {/* Enhanced Overlay for closing menus */}
      {(isUserMenuOpen || isSearchOpen) && (
        <div 
          className="fixed inset-0 z-30 bg-black/10 backdrop-blur-sm" 
          onClick={() => {
            setIsUserMenuOpen(false);
            setIsSearchOpen(false);
          }}
        />
      )}
    </>
  );
};

export default Header;